import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import {
  LogOut,
  Bell,
  DoorOpen,
  DoorClosed,
  Trash2,
  RotateCcw,
  AlarmClockCheck,
  AlertTriangle,
} from "lucide-react";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function formatSigned(seconds) {
  if (seconds == null) seconds = 0;
  const overdue = seconds < 0;
  const abs = Math.abs(seconds);
  const m = Math.floor(abs / 60);
  const s = abs % 60;
  return `${overdue ? "-" : ""}${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const FILTERS = ["all", "waiting", "notified", "inside", "exited"];

export default function AdminDashboard() {
  const { user, checked, logout } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (checked && !user) nav("/admin/login", { replace: true });
  }, [checked, user, nav]);

  const load = useCallback(async () => {
    try {
      const [{ data: q }, { data: s }] = await Promise.all([
        api.get("/admin/queue"),
        api.get("/stats"),
      ]);
      setRows(q);
      setStats(s);
    } catch (e) {
      if (e.response?.status === 401) nav("/admin/login", { replace: true });
    }
  }, [nav]);

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [user, load]);

  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const withRemaining = useMemo(() => {
    void tick;
    
    return rows.map((r) => {
      if (r.status !== "inside" || !r.exit_deadline) return r;
      const deadline = new Date(r.exit_deadline).getTime();
      const remaining = Math.floor((deadline - Date.now()) / 1000);
      return { ...r, remaining_seconds: remaining, is_overdue: remaining <= 0 };
    });
  }, [rows, tick]);

  const filtered = useMemo(() => {
    if (filter === "all") return withRemaining;
    return withRemaining.filter((r) => r.status === filter);
  }, [withRemaining, filter]);

  const action = async (path, method = "post", label) => {
    try {
      await api.request({ url: path, method });
      toast.success(label + " ✓");
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Action failed");
    }
  };

  if (!checked) return <div className="p-10 text-muted-foreground">Loading...</div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-center" richColors />

      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-[100rem] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div>
            <p className="label-eyebrow">okay room · staff</p>
            <p className="font-display text-2xl leading-none mt-1">Queue console</p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-view-public-link"
              className="text-sm link-underline text-muted-foreground hidden sm:inline"
            >
              View public site
            </Link>
            <button
              data-testid="admin-logout-btn"
              onClick={async () => {
                await logout();
                nav("/admin/login", { replace: true });
              }}
              className="inline-flex items-center gap-2 border border-foreground/70 px-3 py-1.5 text-sm hover:bg-foreground hover:text-background transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[100rem] mx-auto px-6 lg:px-10 py-10">
        <section className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-10">
          <BentoStat label="Waiting" value={stats?.waiting ?? 0} tid="admin-stat-waiting" />
          <BentoStat label="Called" value={stats?.notified ?? 0} tid="admin-stat-called" />
          <BentoStat label="Inside" value={`${stats?.inside ?? 0}/${stats?.capacity ?? 30}`} tid="admin-stat-inside" highlight />
          <BentoStat label="Seats left" value={stats?.seats_available ?? 30} tid="admin-stat-seats" />
          <BentoStat label="Overdue" value={stats?.overdue ?? 0} tid="admin-stat-overdue" danger={(stats?.overdue ?? 0) > 0} />
          <BentoStat label="Total people" value={stats?.total_people ?? 0} tid="admin-stat-total-people" />
        </section>

        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex gap-1 border border-border p-1">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`px-4 py-1.5 text-xs uppercase tracking-widest font-semibold transition-colors ${
                  filter === f ? "bg-foreground text-background" : "hover:bg-muted"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid="admin-reset-btn"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-destructive border border-destructive/40 px-3 py-1.5 hover:bg-destructive hover:text-destructive-foreground transition-colors"
              >
                <RotateCcw className="w-3 h-3" /> Reset queue
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear the entire queue?</AlertDialogTitle>
                <AlertDialogDescription>
                  This deletes every registered guest. Use this before an event starts. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="reset-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="reset-confirm"
                  onClick={() => action("/admin/queue/reset", "post", "Queue reset")}
                >
                  Yes, reset
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <div className="border border-border overflow-x-auto" data-testid="admin-queue-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border label-eyebrow text-left">
                <th className="p-3 w-16">#</th>
                <th className="p-3">Guest</th>
                <th className="p-3 w-16">Total</th>
                <th className="p-3 w-36">Status</th>
                <th className="p-3 w-40">Timer</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-10 text-center text-muted-foreground" data-testid="empty-row">
                    No guests in this view yet.
                  </td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`queue-row-${r.queue_number}`}
                  className={`border-b border-border/60 hover:bg-muted/40 transition-colors ${
                    r.status === "inside" && r.is_overdue
                      ? "bg-destructive/10"
                      : r.status === "inside"
                        ? "bg-[hsl(var(--accent))]/30"
                        : ""
                  }`}
                >
                  <td className="p-3 font-mono-num text-base">{String(r.queue_number).padStart(2, "0")}</td>
                  <td className="p-3">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-muted-foreground font-mono-num truncate max-w-[220px]">{r.id.slice(0, 8)}…</div>
                  </td>
                  <td className="p-3 font-mono-num text-base">{r.people_count ?? 1}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={r.status} />
                      {r.status === "inside" && r.timeup_notified_count > 0 && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase font-semibold text-destructive"
                          title={`${r.timeup_notified_count} time-up pings sent`}
                          data-testid={`ping-count-${r.queue_number}`}
                        >
                          <AlertTriangle className="w-3 h-3" strokeWidth={1.6} />×{r.timeup_notified_count}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    {r.status === "inside" ? (
                      <span
                        className={`font-mono-num text-base ${
                          r.is_overdue ? "text-destructive font-semibold" : r.remaining_seconds < 300 ? "text-destructive" : "text-foreground"
                        }`}
                        data-testid={`timer-${r.queue_number}`}
                      >
                        {formatSigned(r.remaining_seconds)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1.5 flex-wrap">
                      {(r.status === "waiting" || r.status === "notified") && (
                        <ActionBtn
                          tid={`call-${r.queue_number}`}
                          icon={Bell}
                          label={r.status === "notified" ? "Re-notify" : "Call"}
                          onClick={() => action(`/admin/queue/${r.id}/call`, "post", "Guest notified")}
                        />
                      )}
                      {(r.status === "waiting" || r.status === "notified") && (
                        <ActionBtn
                          tid={`enter-${r.queue_number}`}
                          icon={DoorOpen}
                          label="Enter"
                          primary
                          onClick={() => action(`/admin/queue/${r.id}/enter`, "post", "Marked entered")}
                        />
                      )}
                      {r.status === "inside" && (
                        <>
                          <ActionBtn
                            tid={`notify-timeup-${r.queue_number}`}
                            icon={AlarmClockCheck}
                            label="Ping time-up"
                            onClick={() => action(`/admin/queue/${r.id}/notify-timeup`, "post", "Time-up ping sent")}
                          />
                          <ActionBtn
                            tid={`exit-${r.queue_number}`}
                            icon={DoorClosed}
                            label="Confirm exit"
                            primary
                            onClick={() => action(`/admin/queue/${r.id}/exit`, "post", "Guest exited")}
                          />
                        </>
                      )}
                      {r.status !== "exited" && (
                        <ActionBtn
                          tid={`delete-${r.queue_number}`}
                          icon={Trash2}
                          label=""
                          danger
                          onClick={() => action(`/admin/queue/${r.id}`, "delete", "Removed")}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

function BentoStat({ label, value, tid, highlight, danger }) {
  return (
    <div
      className={`border p-6 ${
        danger
          ? "border-destructive bg-destructive/10"
          : highlight
            ? "border-border bg-[hsl(var(--accent))]/40"
            : "border-border bg-card"
      }`}
    >
      <p className={`label-eyebrow ${danger ? "text-destructive" : ""}`}>{label}</p>
      <p
        className={`font-mono-num text-4xl mt-2 ${danger ? "text-destructive" : ""}`}
        data-testid={tid}
      >
        {value}
      </p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    waiting: "border-foreground/40 text-foreground",
    notified: "border-[hsl(var(--primary))] text-[hsl(var(--primary))]",
    inside: "border-emerald-700/50 text-emerald-800 bg-emerald-50",
    exited: "border-muted-foreground/30 text-muted-foreground",
    cancelled: "border-muted-foreground/30 text-muted-foreground line-through",
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] tracking-widest uppercase font-semibold border ${map[status] || map.waiting}`}>
      {status}
    </span>
  );
}

function ActionBtn({ icon: Icon, label, onClick, primary, danger, tid }) {
  const base =
    "inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs uppercase tracking-widest font-semibold border transition-colors";
  const style = primary
    ? "border-foreground bg-foreground text-background hover:bg-[hsl(var(--primary))] hover:border-[hsl(var(--primary))]"
    : danger
      ? "border-destructive/40 text-destructive hover:bg-destructive hover:text-destructive-foreground"
      : "border-foreground/40 hover:bg-foreground hover:text-background";
  return (
    <button data-testid={tid} onClick={onClick} className={`${base} ${style}`}>
      <Icon className="w-3 h-3" strokeWidth={1.6} />
      {label && <span>{label}</span>}
    </button>
  );
}
